import { match } from 'ts-pattern'
import type { PolarisStatusState, SkippedEntryReason } from './api-client/api-schemas.ts'

/** Shown wherever a Polaris status could not be read and Polaris itself gave no reason. */
export const POLARIS_STATUS_UNAVAILABLE_MESSAGE = 'Unable to check the current Polaris status'

/**
 * Reports whether a push target's Polaris state leaves the push's effect unconfirmed.
 *
 * Push-confirmation policy rather than part of the wire contract — the spec has no counterpart — so it
 * lives here rather than in `api-schemas.ts`, which is rewritten whenever the OpenAPI spec is synced.
 * Matched exhaustively rather than checked against an allowlist because this gates a write: a state
 * added upstream should break the build here instead of silently defaulting to pushing without asking.
 */
export const getIsPolarisConfirmationRequired = (state: PolarisStatusState) =>
  match(state)
    .with('conflict', 'not-found', 'lookup-failed', () => true)
    .with('match', 'not-applicable', () => false)
    .exhaustive()

/**
 * Explains why a target was left out of the Polaris status lookup.
 *
 * Phrased to work as the object of a "because …" clause and to stand on its own, so the dialog can
 * report a count of skipped findings and the badge can explain a single one without two vocabularies
 * for the same enum.
 */
export const getPolarisSkipReasonPhrase = (reason: SkippedEntryReason) =>
  match(reason)
    .with('scan-disconnected', () => 'the scan is no longer connected')
    .with('no-correlation-id', () => 'no Polaris issue identifier was recorded')
    .with('no-completed-outcome', () => 'the triage has not completed')
    .exhaustive()
